import { useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { useLanguage } from "../../contexts/LanguageContext";
import { subscriptionLabels } from "./labels";
import { landingLabels } from "../landingPage/labels";
import { formatDate } from "../utils/date";
import { centsToCurrency } from "../utils/money";
import type { CompanySubscription, CompanySubscriptionInvoice } from "../../types";
import { PricingSection } from "../landingPage/components/PricingSection";

export default function SubscriptionPage() {
  const { lang } = useLanguage();
  const t = subscriptionLabels[lang];
  const planLabels = landingLabels[lang].pricing.plans;
  const [sub, setSub] = useState<CompanySubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<CompanySubscriptionInvoice[] | null>(null);
  const now = new Date();
  

  useEffect(() => {
    (async () => {
      const subRes = await api.getCompanySubscription();
      if (subRes.ok) setSub(subRes.data as any);
      const invRes = await api.getCompanySubscriptionInvoices();
      if (invRes.ok) setInvoices(invRes.data);
      setLoading(false);
    })();
  }, []);

  

  const openPortal = async () => {
    const res = await api.createStripePortal();
    if (res.ok) window.location.href = res.data.url;
  };

  const subscribePlan = async (plan: string) => {
    const res = await api.createStripeCheckout(plan);
    if (res.ok) window.location.href = res.data.url;
  };

  if (loading) return <p>{t.loading}</p>;

  const isExpired = !!sub?.next_renewal && new Date(sub.next_renewal) <= now;

  return (
    <div className="space-y-4">
        {sub && !isExpired ? (
          <div className="rounded-lg border bg-white p-6 shadow-sm space-y-4">
            <div>
              <h3 className="text-lg font-medium">
                {t.currentPlan.replace(
                  "{plan}",
                  planLabels[sub.plan as keyof typeof planLabels]?.name || sub.plan
                )}
              </h3>
              <div className="text-sm">
                {isExpired ? (
                  <p className="text-red-700">
                    {(t.expiredSince ?? "Plano inativo desde")} {formatDate(sub.next_renewal, { dateStyle: "medium" })}
                  </p>
                ) : (
                  <p className="text-neutral-500">
                    {t.nextRenewal}: {formatDate(sub.next_renewal, { dateStyle: "medium" })}
                  </p>
                )}
              </div>
            </div>
            <div className="text-xl font-bold">{centsToCurrency(sub.price_cents, lang)}</div>
            <Button variant="outline" onClick={openPortal}>
              {t.manage}
            </Button>
          </div>
        ) : (
          <PricingSection onSelectPlan={subscribePlan} />
        )}

        {invoices && invoices.length > 0 && (
          <div className="rounded-lg border bg-white p-6 shadow-sm">
            <h4 className="text-md font-medium mb-3">{t.historyTitle ?? "Histórico de pagamentos"}</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-500">
                    <th className="py-2 pr-4">{t.period ?? "Período"}</th>
                    <th className="py-2 pr-4">{t.amount ?? "Valor"}</th>
                    <th className="py-2 pr-4">{t.status ?? "Status"}</th>
                    <th className="py-2 pr-4">{t.paidAt ?? "Pago em"}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const paid = !!inv.paid_at;
                    const refunded = (inv.refunded_amount_cents ?? 0) > 0;
                    const fullRefund = refunded && (inv.refunded_amount_cents ?? 0) >= inv.amount_cents;
                    return (
                      <tr key={inv.id} className="border-t">
                        <td className="py-2 pr-4">
                          {formatDate(inv.period_start, { dateStyle: "medium" })} — {formatDate(inv.period_end, { dateStyle: "medium" })}
                        </td>
                        <td className="py-2 pr-4">{centsToCurrency(inv.amount_cents, lang)}</td>
                        <td className={`py-2 pr-4 ${refunded ? "text-amber-700" : paid ? "text-green-700" : "text-red-700"}`}>
                          {refunded ? (fullRefund ? (t.refunded ?? "Estornado") : (t.partiallyRefunded ?? "Estornado parcial")) : paid ? (t.paid ?? "Pago") : (t.failed ?? "Falhou")}
                        </td>
                        <td className="py-2 pr-4">{inv.paid_at ? formatDate(inv.paid_at, { dateStyle: "medium" }) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
}
